/* ============================================================
 * WorkshopSection – Kostenlose Live-Workshops
 * Zwei Karten: Beauty (Dienstag) + Health (Donnerstag)
 * Countdown-Timer zum nächsten Termin
 * + Dosha-Schnelltest Integration
 * Design: Dark section, gold accents, olive CTAs
 * ============================================================ */
import { useState, useEffect, useCallback } from "react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { Sparkles, Heart, Calendar, Clock, ArrowRight, Users, Zap } from "lucide-react";
import { IMAGES } from "@/lib/constants";

/* ── helpers ─────────────────────────────────────────────── */

/**
 * Return the next occurrence of a given weekday (0=Sun … 6=Sat) at 19:00 CET/CEST.
 */
function getNextOccurrence(targetDay: number): Date {
  const now = new Date();
  const berlinStr = now.toLocaleString("en-US", { timeZone: "Europe/Berlin" });
  const berlinNow = new Date(berlinStr);

  let diff = (targetDay - berlinNow.getDay() + 7) % 7;
  if (diff === 0 && berlinNow.getHours() >= 19) {
    diff = 7;
  }

  const targetBerlin = new Date(berlinNow);
  targetBerlin.setDate(berlinNow.getDate() + diff);
  targetBerlin.setHours(19, 0, 0, 0);

  const berlinOffsetMs = berlinNow.getTime() - now.getTime();
  return new Date(targetBerlin.getTime() - berlinOffsetMs);
}

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

function useCountdown(targetDay: number): TimeLeft {
  const calcTimeLeft = useCallback((): TimeLeft => {
    const target = getNextOccurrence(targetDay);
    const diff = Math.max(0, target.getTime() - Date.now());
    return {
      days: Math.floor(diff / (1000 * 60 * 60 * 24)),
      hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((diff / (1000 * 60)) % 60),
      seconds: Math.floor((diff / 1000) % 60),
    };
  }, [targetDay]);

  const [timeLeft, setTimeLeft] = useState<TimeLeft>(calcTimeLeft);

  useEffect(() => {
    const id = setInterval(() => setTimeLeft(calcTimeLeft()), 1000);
    return () => clearInterval(id);
  }, [calcTimeLeft]);

  return timeLeft;
}

/* ── data ────────────────────────────────────────────────── */

const WORKSHOPS = [
  {
    id: "beauty",
    badge: "NEU",
    day: 2, // Dienstag
    dayLabel: "Jeden Dienstag",
    time: "19:00 Uhr",
    title: "AYU Beauty Workshop",
    subtitle: "Wahre Schönheit beginnt im Inneren",
    description:
      "Strahlende Haut, glänzende Haare & starke Nägel — entdecke, wie AYU ayurvedische Beauty-Rituale personalisiert für dich übersetzt.",
    highlights: ["Dein Hauttyp entschlüsselt", "Beauty-Rituale die wirken", "Ernährung für strahlende Haut"],
    url: "https://masterclass.ayu-rveda.ai/ayu-beauty",
    icon: Sparkles,
    accentColor: "oklch(0.78 0.11 85)", // Gold
    accentBg: "oklch(0.78 0.11 85 / 0.08)",
    accentBorder: "oklch(0.78 0.11 85 / 0.25)",
  },
  {
    id: "health",
    badge: null,
    day: 4, // Donnerstag
    dayLabel: "Jeden Donnerstag",
    time: "19:00 Uhr",
    title: "AYU Live Workshop",
    subtitle: "Entdecke, was dein Körper wirklich braucht",
    description:
      "In dieser Masterclass zeigt dir Annam Hanfstein, wie AYU 5.000 Jahre Ayurveda-Weisheit personalisiert für dich nutzbar macht.",
    highlights: ["12 Phänotypen verstehen", "Personalisierter Gesundheitsplan", "Live Q&A mit Annam"],
    url: "https://masterclass.ayu-rveda.ai/ayu-workshop",
    icon: Heart,
    accentColor: "oklch(0.52 0.09 128)", // Olive
    accentBg: "oklch(0.52 0.09 128 / 0.08)",
    accentBorder: "oklch(0.52 0.09 128 / 0.25)",
  },
] as const;

/* ── countdown digit ─────────────────────────────────────── */

function CountdownDigit({ value, label }: { value: number; label: string }) {
  return (
    <div className="flex flex-col items-center">
      <div className="relative w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center rounded-xl bg-[oklch(0.15_0.02_280)] border border-[oklch(0.78_0.11_85/0.2)] shadow-lg">
        <span
          className="text-2xl sm:text-3xl font-bold text-[oklch(0.78_0.11_85)] tabular-nums"
          style={{ fontFamily: "var(--font-display)" }}
        >
          {String(value).padStart(2, "0")}
        </span>
      </div>
      <span className="text-[10px] sm:text-xs uppercase tracking-widest text-[oklch(0.6_0.02_80)] mt-2">
        {label}
      </span>
    </div>
  );
}

/* ── workshop card ───────────────────────────────────────── */

function WorkshopCard({
  workshop,
  index,
  isVisible,
}: {
  workshop: (typeof WORKSHOPS)[number];
  index: number;
  isVisible: boolean;
}) {
  const countdown = useCountdown(workshop.day);
  const Icon = workshop.icon;

  return (
    <div
      className={`relative group rounded-3xl overflow-hidden transition-all duration-700 ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
      }`}
      style={{
        transitionDelay: `${index * 200 + 200}ms`,
        background: "linear-gradient(145deg, oklch(0.16 0.02 280), oklch(0.13 0.02 280))",
        border: `1px solid ${workshop.accentBorder}`,
      }}
    >
      {/* Glow effect on hover */}
      <div
        className="absolute -inset-1 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl pointer-events-none"
        style={{ background: `${workshop.accentColor.replace(")", " / 0.08)")}` }}
      />

      {/* NEU Badge */}
      {workshop.badge && (
        <div className="absolute top-5 right-5 z-10">
          <span
            className="px-3 py-1 rounded-full text-xs font-bold tracking-wider uppercase animate-pulse"
            style={{
              background: workshop.accentColor,
              color: "oklch(0.12 0.02 280)",
            }}
          >
            {workshop.badge}
          </span>
        </div>
      )}

      <div className="relative p-7 sm:p-9">
        {/* Header: Icon + Day/Time */}
        <div className="flex items-start gap-4 mb-6">
          <div
            className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0"
            style={{ background: workshop.accentBg, border: `1px solid ${workshop.accentBorder}` }}
          >
            <Icon size={26} style={{ color: workshop.accentColor }} />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Calendar size={14} style={{ color: workshop.accentColor }} />
              <span className="text-sm font-medium" style={{ color: workshop.accentColor }}>
                {workshop.dayLabel}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Clock size={14} className="text-[oklch(0.6_0.02_80)]" />
              <span className="text-sm text-[oklch(0.6_0.02_80)]">{workshop.time} — Online & kostenlos</span>
            </div>
          </div>
        </div>

        {/* Title */}
        <h3
          className="text-2xl sm:text-3xl font-bold text-[oklch(0.95_0.01_80)] mb-2 leading-tight"
          style={{ fontFamily: "var(--font-display)" }}
        >
          {workshop.title}
        </h3>
        <p
          className="text-base italic mb-4"
          style={{ color: workshop.accentColor, fontFamily: "var(--font-display)" }}
        >
          {workshop.subtitle}
        </p>

        {/* Description */}
        <p
          className="text-sm sm:text-base text-[oklch(0.7_0.02_80)] leading-relaxed mb-6"
          style={{ fontFamily: "var(--font-body)" }}
        >
          {workshop.description}
        </p>

        {/* Highlights */}
        <div className="space-y-2.5 mb-6">
          {workshop.highlights.map((h, i) => (
            <div key={i} className="flex items-center gap-3">
              <div
                className="w-5 h-5 rounded-full flex items-center justify-center shrink-0"
                style={{ background: workshop.accentBg, border: `1px solid ${workshop.accentBorder}` }}
              >
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                  <path d="M2 5L4.5 7.5L8 3" stroke={workshop.accentColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <span className="text-sm text-[oklch(0.8_0.01_80)]">{h}</span>
            </div>
          ))}
        </div>

        {/* ── Dosha-Schnelltest Hinweis ──────────────────── */}
        <div
          className="rounded-2xl p-4 sm:p-5 mb-6"
          style={{
            background: "linear-gradient(135deg, oklch(0.78 0.11 85 / 0.08), oklch(0.52 0.09 128 / 0.08))",
            border: "1px solid oklch(0.78 0.11 85 / 0.2)",
          }}
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-[oklch(0.78_0.11_85/0.15)] border border-[oklch(0.78_0.11_85/0.3)] shrink-0">
              <Zap size={18} className="text-[oklch(0.78_0.11_85)]" />
            </div>
            <div>
              <p className="text-sm font-bold text-[oklch(0.93_0.015_80)]">
                NEU: Dosha-Dysbalance-Schnelltest
              </p>
              <p className="text-xs text-[oklch(0.6_0.02_80)] mt-0.5">
                Fülle den 2-Minuten-Test live im Workshop aus — erhalte sofort deine persönliche Analyse mit konkreter Aufklärung, was bei dir das Problem ist.
              </p>
            </div>
          </div>
        </div>

        {/* Countdown */}
        <div className="mb-8">
          <p className="text-xs uppercase tracking-[0.15em] text-[oklch(0.55_0.02_80)] mb-3">
            Der nächste Workshop startet in:
          </p>
          <div className="flex items-center gap-2 sm:gap-3">
            <CountdownDigit value={countdown.days} label="Tage" />
            <span className="text-xl text-[oklch(0.78_0.11_85/0.4)] font-light mt-[-18px]">:</span>
            <CountdownDigit value={countdown.hours} label="Std" />
            <span className="text-xl text-[oklch(0.78_0.11_85/0.4)] font-light mt-[-18px]">:</span>
            <CountdownDigit value={countdown.minutes} label="Min" />
            <span className="text-xl text-[oklch(0.78_0.11_85/0.4)] font-light mt-[-18px]">:</span>
            <CountdownDigit value={countdown.seconds} label="Sek" />
          </div>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Primary: Workshop anmelden */}
          <a
            href={workshop.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full text-base font-semibold tracking-wide transition-all duration-300 group/btn"
            style={{
              background: workshop.id === "beauty" ? "oklch(0.78 0.11 85)" : "oklch(0.52 0.09 128)",
              color: workshop.id === "beauty" ? "oklch(0.12 0.02 280)" : "oklch(0.97 0.01 80)",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.boxShadow = `0 0 30px ${workshop.accentColor.replace(")", " / 0.3)")}`;
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.boxShadow = "none";
            }}
          >
            Jetzt kostenlos teilnehmen
            <ArrowRight size={18} className="group-hover/btn:translate-x-1 transition-transform" />
          </a>


        </div>

        {/* Participants hint */}
        <div className="flex items-center gap-2 mt-5">
          <Users size={14} className="text-[oklch(0.55_0.02_80)]" />
          <span className="text-xs text-[oklch(0.55_0.02_80)]">180.000+ Menschen bereits begleitet</span>
        </div>
      </div>
    </div>
  );
}

/* ── main section ────────────────────────────────────────── */

export default function WorkshopSection() {
  const { ref, isVisible } = useScrollAnimation(0.05);

  return (
    <>
      <section id="workshops" className="section-dark py-24 lg:py-32 relative overflow-hidden" ref={ref}>
        {/* Mandala overlay */}
        <div className="absolute inset-0 mandala-overlay pointer-events-none" />

        {/* Decorative glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-[oklch(0.78_0.11_85/0.03)] blur-[120px] pointer-events-none" />

        <div className="container relative z-10">
          {/* Section Header */}
          <div
            className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-700 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            <span
              className="text-sm font-semibold tracking-[0.2em] uppercase text-[oklch(0.52_0.09_128)]"
              style={{ fontFamily: "var(--font-body)" }}
            >
              Kostenlose Live-Workshops
            </span>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[oklch(0.93_0.015_80)] mt-4 mb-6"
              style={{ fontFamily: "var(--font-display)" }}
            >
              Erlebe <span className="text-[oklch(0.78_0.11_85)]">AYU</span> live
            </h2>
            <p
              className="text-lg text-[oklch(0.7_0.02_80)] leading-relaxed"
              style={{ fontFamily: "var(--font-body)" }}
            >
              Jede Woche zwei kostenlose Workshops mit Annam Hanfstein — lerne Ayurveda kennen
              und entdecke, was dein Körper wirklich braucht.
              {" "}
              <span className="text-[oklch(0.78_0.11_85)] font-medium">
                Mach den Dosha-Schnelltest und erhalte deine Live-Analyse im Workshop!
              </span>
            </p>
          </div>

          {/* Workshop Cards */}
          <div className="grid md:grid-cols-2 gap-8 lg:gap-10">
            {WORKSHOPS.map((ws, i) => (
              <WorkshopCard
                key={ws.id}
                workshop={ws}
                index={i}
                isVisible={isVisible}
              />
            ))}
          </div>
        </div>
      </section>

    </>
  );
}
